import { c as _c } from "react/compiler-runtime";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { DEFAULT_TEAMS_SORT_BY } from "@components/teams/teams-constants";
import { TeamsSort } from "@components/teams/teams-sort";
import { isTeamsSortBy } from "@custom-types/type-guards";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { useTeamsPageBulkActions } from "@pages/members/teams/use-teams-page-bulk-actions";
import { shouldShowTeamsPremiumPage, TeamsPremiumFeaturePage } from "@pages/members/teams/teams-premium-feature-page";
import { MemberTeamsDetails } from "@pages/profile/member-teams-details";
import { MemberTeamsTableWrapper } from "@pages/profile/member-teams-table-wrapper";
import { useAppParams } from "@router/router-helper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { companyMemberSelector } from "@store/members/members-selector";
import { canUseTeamsSelector } from "@store/sdb-company/sdb-company-selector";
import { allMemberTeamsSelector, memberTeamsSelector, memberTeamsStatusSelector } from "@store/teams/teams-selector";
import { resetMemberTeams } from "@store/teams/teams-slice";
import { fetchTeams } from "@store/teams/teams-slice-thunk";
import { getExportComponent } from "@utils/member-utils";
import { sortTeams } from "@utils/team-utils";
import { useEffect, useMemo } from "react";

/**
 * Lists all the teams the selected member belongs to, with the same table
 * as the teams page but restricted to "View details" / "Change role" actions.
 */
export function MemberTeamsPage() {
  const $ = _c(39);
  const {
    memberId
  } = useAppParams();
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const teams = useAppSelector(memberTeamsSelector);
  const allMemberTeams = useAppSelector(allMemberTeamsSelector);
  const status = useAppSelector(memberTeamsStatusSelector);
  let t0;
  if ($[0] !== memberId) {
    t0 = companyMemberSelector(memberId);
    $[0] = memberId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const member = useAppSelector(t0);
  const canUseTeams = useAppSelector(canUseTeamsSelector);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultSort: DEFAULT_TEAMS_SORT_BY,
      isValidSort: isTeamsSortBy
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    sortBy,
    setSortBy
  } = useSubjectsSortByQuery(t1);
  let t2;
  if ($[3] !== sortBy || $[4] !== teams) {
    t2 = sortTeams(teams, sortBy);
    $[3] = sortBy;
    $[4] = teams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const sortedTeams = t2;
  let t3;
  let t4;
  if ($[6] !== dispatch || $[7] !== memberId) {
    t3 = () => {
      if (memberId) {
        dispatch(fetchTeams({
          memberIdentity: memberId
        }));
      }
      return () => {
        dispatch(resetMemberTeams());
      };
    };
    t4 = [dispatch, memberId];
    $[6] = dispatch;
    $[7] = memberId;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== companyId || $[11] !== teams) {
    t5 = {
      companyId,
      teams,
      shouldExcludeRemove: true
    };
    $[10] = companyId;
    $[11] = teams;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const buttonComponents = useTeamsPageBulkActions(t5);
  const isLoading = status === "pending" || status === "uninitialized";
  const shouldRenderActions = sortedTeams.length > 0;
  if (shouldShowTeamsPremiumPage({
    canUseTeams,
    teams: allMemberTeams,
    status
  })) {
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <TeamsPremiumFeaturePage />;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t6;
  if ($[14] !== teams.length) {
    t6 = <MemberTeamsDetails teamsCount={teams.length} />;
    $[14] = teams.length;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = teams.length;
  let t8;
  if ($[16] !== setSortBy || $[17] !== shouldRenderActions || $[18] !== sortBy) {
    t8 = shouldRenderActions && <TeamsSort key="teams-sort" sortBy={sortBy} setSortBy={setSortBy} />;
    $[16] = setSortBy;
    $[17] = shouldRenderActions;
    $[18] = sortBy;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== buttonComponents.export || $[21] !== teams.length) {
    t9 = getExportComponent(teams.length, buttonComponents.export);
    $[20] = buttonComponents.export;
    $[21] = teams.length;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== isLoading || $[24] !== t8 || $[25] !== t9 || $[26] !== teams.length) {
    t10 = <EntitiesToolbar toolbarEntityName="Team" numberOfRows={t7} isLoading={isLoading} sortActions={t8} exportComponent={t9} />;
    $[23] = isLoading;
    $[24] = t8;
    $[25] = t9;
    $[26] = teams.length;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== buttonComponents || $[29] !== companyId || $[30] !== member || $[31] !== setSortBy || $[32] !== sortBy || $[33] !== sortedTeams) {
    t11 = <MemberTeamsTableWrapper companyId={companyId} sortedTeams={sortedTeams} buttonComponents={buttonComponents} sortBy={sortBy} setSortBy={setSortBy} member={member} />;
    $[28] = buttonComponents;
    $[29] = companyId;
    $[30] = member;
    $[31] = setSortBy;
    $[32] = sortBy;
    $[33] = sortedTeams;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t6) {
    t12 = <>{t6}{t10}{t11}</>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t6;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
}